import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BookOpen,
  CalendarClock,
  ClipboardCheck,
  Clock3,
  TrendingDown,
  Users,
} from 'lucide-react';
import {
  Avatar,
  Badge,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatCard,
} from '@tutor/ui';
import { useTutorDashboard } from '../../hooks/queries';
import { LessonCard } from '../../components/LessonCard';
import { HomeworkCard } from '../../components/HomeworkCard';
import { formatAgo, formatRelativeDayTime } from '../../lib/format';

const SEVERITY_TONE = { INFO: 'info', WARNING: 'warning', CRITICAL: 'danger' } as const;

export function TutorDashboardPage() {
  const { data, isLoading, isError, refetch } = useTutorDashboard();

  if (isLoading) return <LoadingState label="Loading your dashboard…" />;
  if (isError || !data) {
    return (
      <ErrorState
        description="We could not load your dashboard."
        action={
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            Try again
          </button>
        }
      />
    );
  }

  return (
    <>
      <PageHeader
        title={`Good ${data.greeting.partOfDay}, ${data.greeting.name}`}
        description="Here is what needs your attention today."
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Today's Lessons"
          value={data.stats.todaysLessons}
          hint={`${data.stats.hoursTaughtThisWeek} hrs taught this week`}
          icon={<BookOpen className="h-4 w-4" aria-hidden />}
          tone="brand"
        />
        <StatCard
          label="Homework to mark"
          value={data.stats.homeworkToMark}
          hint={data.stats.homeworkToMark > 0 ? 'Waiting on you' : 'All caught up'}
          icon={<ClipboardCheck className="h-4 w-4" aria-hidden />}
          tone={data.stats.homeworkToMark > 0 ? 'warning' : 'success'}
        />
        <StatCard
          label="Active students"
          value={data.stats.activeStudents}
          icon={<Users className="h-4 w-4" aria-hidden />}
        />
        <StatCard
          label="Upcoming assessments"
          value={data.stats.upcomingAssessments}
          icon={<CalendarClock className="h-4 w-4" aria-hidden />}
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader
              title="Today's Lessons"
              action={
                <Link to="/tutor/calendar" className="text-sm font-medium text-brand-600 hover:text-brand-700">
                  View calendar
                </Link>
              }
            />
            <CardBody className="space-y-3">
              {data.todaysLessons.length === 0 ? (
                <EmptyState
                  title="No lessons today"
                  description="Enjoy the quiet — or schedule something in."
                  icon={<BookOpen className="h-5 w-5" aria-hidden />}
                />
              ) : (
                data.todaysLessons.map((lesson) => (
                  <LessonCard key={lesson.id} lesson={lesson} perspective="tutor" />
                ))
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Homework Awaiting Marking"
              description={
                data.homeworkAwaitingMarking.length > 0
                  ? `${data.homeworkAwaitingMarking.length} submission${data.homeworkAwaitingMarking.length === 1 ? '' : 's'} to review`
                  : undefined
              }
              action={
                <Link to="/tutor/homework" className="text-sm font-medium text-brand-600 hover:text-brand-700">
                  View all
                </Link>
              }
            />
            <CardBody className="space-y-3">
              {data.homeworkAwaitingMarking.length === 0 ? (
                <EmptyState
                  title="Nothing to mark"
                  description="Every submission has been marked."
                  icon={<ClipboardCheck className="h-5 w-5" aria-hidden />}
                />
              ) : (
                data.homeworkAwaitingMarking.map((assignment) => (
                  <HomeworkCard key={assignment.id} assignment={assignment} perspective="tutor" />
                ))
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Upcoming Lessons" />
            <CardBody className="space-y-3">
              {data.upcomingLessons.length === 0 ? (
                <EmptyState title="Nothing scheduled beyond today" icon={<CalendarClock className="h-5 w-5" aria-hidden />} />
              ) : (
                data.upcomingLessons.map((lesson) => (
                  <LessonCard key={lesson.id} lesson={lesson} perspective="tutor" showDay />
                ))
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Student Progress Alerts" />
            <CardBody>
              {data.progressAlerts.length === 0 ? (
                <EmptyState
                  title="No alerts"
                  description="Every student is tracking to target."
                  icon={<TrendingDown className="h-5 w-5" aria-hidden />}
                />
              ) : (
                <ul className="space-y-3">
                  {data.progressAlerts.map((alert) => (
                    <li key={`${alert.studentId}-${alert.subjectName}`}>
                      <Link
                        to={`/tutor/students/${alert.studentId}`}
                        className="flex items-start gap-3 rounded-lg p-2 hover:bg-slate-50"
                      >
                        <Avatar name={alert.studentName} src={alert.avatarUrl} size="sm" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-medium text-slate-900">{alert.studentName}</p>
                            <Badge tone={SEVERITY_TONE[alert.severity]}>{alert.severity.toLowerCase()}</Badge>
                          </div>
                          <p className="mt-0.5 text-sm text-slate-600">{alert.reason}</p>
                          <p className="mt-0.5 text-xs text-slate-400">{alert.metric}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Recently Submitted" />
            <CardBody>
              {data.recentlySubmittedHomework.length === 0 ? (
                <EmptyState title="No recent submissions" />
              ) : (
                <ul className="space-y-3">
                  {data.recentlySubmittedHomework.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/tutor/homework/${item.id}`}
                        className="flex items-start gap-3 rounded-lg p-2 hover:bg-slate-50"
                      >
                        <Avatar name={item.student.fullName} src={item.student.avatarUrl} size="sm" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-900">{item.student.fullName}</p>
                          <p className="truncate text-sm text-slate-600">{item.homeworkTitle}</p>
                          {item.submittedAt && (
                            <p className="mt-0.5 text-xs text-slate-400">{formatAgo(item.submittedAt)}</p>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Upcoming Assessments" />
            <CardBody>
              {data.upcomingAssessments.length === 0 ? (
                <EmptyState title="None scheduled" icon={<AlertTriangle className="h-5 w-5" aria-hidden />} />
              ) : (
                <ul className="space-y-3">
                  {data.upcomingAssessments.map((assessment) => (
                    <li key={assessment.id} className="rounded-lg border border-slate-100 p-3">
                      <p className="text-sm font-medium text-slate-900">{assessment.title}</p>
                      <p className="mt-0.5 text-sm text-slate-600">{assessment.studentName}</p>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
                        <Clock3 className="h-3 w-3" aria-hidden />
                        {formatRelativeDayTime(assessment.scheduledFor)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Recent Activity" />
            <CardBody>
              {data.recentActivity.length === 0 ? (
                <EmptyState title="No recent activity" />
              ) : (
                <ul className="space-y-3">
                  {data.recentActivity.map((item) => (
                    <li key={item.id} className="border-l-2 border-slate-100 pl-3">
                      <p className="text-sm text-slate-900">{item.title}</p>
                      {item.description && <p className="text-sm text-slate-500">{item.description}</p>}
                      <p className="mt-0.5 text-xs text-slate-400">{formatAgo(item.occurredAt)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}
